<i18n>
{
  "en": {
    "calendar": "Event occurrence calendar",
    "close": "Close calendar",
    "today": "Today",
    "lang": "en-EN",
    "pointOfSale": "Filter by point of sale"
  },
  "es": {
    "calendar": "Calendario de ocurrencias de eventos",
    "close": "Cerrar calendario",
    "today": "Hoy",
    "lang": "es-ES",
    "pointOfSale": "Filtrar por punto de venta"
  }
}
</i18n>
<template>
  <v-dialog v-model="visible"
            max-width="70%" min-width="800px"
            persistent>
    <v-card v-if="visible" persistent class="px-7 py-4">
      <v-row mb-3 class="d-flex justify-space-between">
        <v-card-title class="pa-0 mb-2">
          {{ $t('calendar') }}
        </v-card-title>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon class="plus-icon mr-0" v-on="on" v-bind="attrs"
                   @click="onCloseModal">
              <v-icon color="grey" large>mdi-close</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('close') }}</span>
        </v-tooltip>
      </v-row>
      <v-divider class="mb-3"/>
      <v-sheet height="750px" class="d-flex flex-column">
        <v-toolbar flat>
          <v-btn outlined color="grey darken-2" @click="setToday">{{ $t('today') }}</v-btn>
          <v-btn fab text small color="grey darken-2" @click="prev">
            <v-icon small>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn fab text small color="grey darken-2" @click="next">
            <v-icon small>mdi-chevron-right</v-icon>
          </v-btn>
          <v-toolbar-title>{{ dateTitle }}</v-toolbar-title>

          <v-spacer></v-spacer>

          <v-col cols="auto" class="d-flex align-end">
            <v-select v-model="selectedPos" :items="pointOfSaleList"
                      item-value="guid" item-text="name"
                      :label="this.$t('pointOfSale')"
                      dense hide-details chips clearable/>
          </v-col>
        </v-toolbar>
        <v-calendar
          ref="calendar"
          v-model="focus"
          :event-color="getEventColor"
          :events="formattedEventList"
          @change="updateTitle"
          :locale="this.$t('lang')"
          :weekdays="[1, 2, 3, 4, 5, 6, 0]"
        ></v-calendar>
      </v-sheet>
    </v-card>
  </v-dialog>
</template>

<script>
import Component from 'vue-class-component';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import { Prop } from 'vue-property-decorator';
import Vue from 'vue';

@Component
@Components({ })
@MapGetters(['eventOccurrenceCalendarList', 'pointOfSaleList'])
@MapActions([])
export default class OccurrenceCalendarModal extends Vue {
  @Prop({ Boolean, default: false }) visible;

  valid = false;

  focus = new Date().toISOString().slice(0, 10);

  dateTitle = '';

  selectedPos = '';

  async created() {
    this.updateTitle();
  }

  get formattedEventList() {
    const filteredByPos = this.eventOccurrenceCalendarList
      .filter((event) => !this.selectedPos || event.posGuidList.includes(this.selectedPos));

    const groupedEventList = this.groupConsecutiveEvents(filteredByPos);

    return groupedEventList.map((event) => ({
      name: `${event.name.toUpperCase()}: ${event.description}`,
      start: event.start,
      end: event.end,
      timed: false,
      color: event.color
    }));
  }

  getEventColor(event) {
    return event.color;
  }

  sortEventsByDate(eventList) {
    return eventList.sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  groupConsecutiveEvents(eventList) {
    const sortedEventList = this.sortEventsByDate(eventList);
    const groupedEvents = [];
    let currentEventGroup = null;

    sortedEventList.forEach((event) => {
      if (currentEventGroup && event.name === currentEventGroup.name
        && event.description === currentEventGroup.description
        && this.isConsecutiveDate(currentEventGroup.end, event.date)) {
        currentEventGroup.end = event.date;
      } else {
        if (currentEventGroup) {
          groupedEvents.push(currentEventGroup);
        }
        currentEventGroup = { ...event, start: event.date, end: event.date };
      }
    });

    if (currentEventGroup) {
      groupedEvents.push(currentEventGroup);
    }

    return groupedEvents;
  }

  isConsecutiveDate(date1, date2) {
    const nextDay = new Date(date1);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay.toISOString().slice(0, 10) === date2;
  }

  onCloseModal() {
    this.$emit('cancel');
    this.setToday();
    this.selectedPos = '';
  }

  setToday() {
    this.focus = new Date().toISOString().slice(0, 10);
  }

  prev() {
    this.changeMonth(-1);
  }

  next() {
    this.changeMonth(1);
  }

  changeMonth(step) {
    const date = new Date(this.focus);
    date.setMonth(date.getMonth() + step);
    this.focus = date.toISOString().slice(0, 10);
  }

  updateTitle() {
    const date = new Date(this.focus);
    this.dateTitle = date.toLocaleDateString(this.$t('lang'), { month: 'long', year: 'numeric' });
  }
}
</script>

<style scoped>

</style>
